import moment from "moment";
import {
  CustomRegistrationFormData,
  ProcessDataConfig,
  SocialAuthResponse,
} from "./types";

export const CACHE_TTL = 2 * 60 * 1000;

export const CHECKBOX_VALUE_SEPARATOR = "";

export const CHECKBOX_VALUE_DISPLAY_SEPARATOR = ", ";

export const isCacheValid = (cacheTimestamp?: number): boolean => {
  if (!cacheTimestamp) return false;
  const now = Date.now();
  const cacheAge = now - cacheTimestamp;
  return cacheAge < CACHE_TTL;
};

const isFile = (value: unknown): value is File => {
  return value instanceof File;
};

const FORM_ID_PATTERN = /^[a-f0-9]{4,}$/;
const BOOKING_TYPE_PATTERN = /^typ_[a-f0-9]+$/;

const bookingTypeKey = (processId: string) => `sum:booking-type:${processId}`;

const asBookingType = (value: string | null) =>
  value && BOOKING_TYPE_PATTERN.test(value) ? value : null;

/**
 * The appointment type whose page sent the client to sign up, read from the address on arrival and kept for the
 * tab, so that a reload during the registration does not lose it.
 */
export const captureBookingType = (
  processId: string,
  search: string
): string | null => {
  const arrived = asBookingType(
    new URLSearchParams(search).get("booking_type")
  );
  try {
    if (arrived) {
      sessionStorage.setItem(bookingTypeKey(processId), arrived);
      return arrived;
    }
    return asBookingType(sessionStorage.getItem(bookingTypeKey(processId)));
  } catch {
    return arrived;
  }
};

export const forgetBookingType = (processId: string) => {
  try {
    sessionStorage.removeItem(bookingTypeKey(processId));
  } catch {}
};

/** The form id the server knows: a form of its own, or "default", the form of before the multiple forms. */
export const toPublicFormId = (formId?: string) =>
  formId && FORM_ID_PATTERN.test(formId) ? formId : "default";

export const createBasicAuthFromAccessToken = (
  response: SocialAuthResponse | null
): string => {
  if (!response) return "";
  const auth = btoa(`${response.user_identification}:${response.access_token}`);
  return `Basic ${auth}`;
};

export const prepareRegistrationPayload = (
  data: CustomRegistrationFormData,
  config: ProcessDataConfig | undefined
) => {
  const payload: { [key: string]: string | undefined } = {
    ...data,
    user_confirm_password: data.user_password,
    user_birth_date: data.user_birth_date
      ? moment(data.user_birth_date).format("YYYY-MM-DD").toString()
      : undefined,
    user_role: "default",
    user_type: "default",
  };

  Object.keys(payload).forEach((key) => {
    const value = payload[key as keyof typeof payload];
    if (value === "" || value === null || value === undefined) {
      delete payload[key as keyof typeof payload];
    }

    if (key === "user_birth_date" && value === "Invalid date") {
      delete payload[key as keyof typeof payload];
    }
  });

  const answers: Record<
    string,
    string | { file_name: string; extension: string }
  > = {};
  const fileQuestions: Array<{ question: string; file: File; index: number }> =
    [];

  config?.customQuestions?.forEach((element, index) => {
    const questionKey = `question_${index}`;
    const value = payload[questionKey] as string | File | null | undefined;

    if (value !== undefined && value !== null && value !== "") {
      if (isFile(value)) {
        fileQuestions.push({ question: element.question, file: value, index });
        delete payload[questionKey];
      } else {
        const stringValue = String(value);
        answers[element.question] = stringValue.includes(
          CHECKBOX_VALUE_SEPARATOR
        )
          ? stringValue
              .split(CHECKBOX_VALUE_SEPARATOR)
              .filter(Boolean)
              .join(CHECKBOX_VALUE_DISPLAY_SEPARATOR)
          : stringValue;
        delete payload[questionKey];
      }
    }
  });

  return { payload, answers, fileQuestions };
};

export const prepareUpdatePayload = (data: CustomRegistrationFormData) => {
  return {
    new_user_birth_date: data.user_birth_date
      ? moment(data.user_birth_date).format("YYYY-MM-DD").toString()
      : undefined,
    new_user_country_code: data.user_country_code,
    new_user_first_name: data.user_first_name,
    new_user_last_name: data.user_last_name,
    new_user_phone: data.user_phone,
    new_user_user_name: data.user_user_name,
  };
};
